import { Container, Matrix } from "pixi.js";
import { NULL_LOG, type Log } from "../log/Log";
import type { Bone, Slot, Spine } from "@esotericsoftware/spine-pixi-v8";

interface BoneSocket {
  follower: Container;
  bone: Bone;
  /** When set, the follower's alpha tracks this slot's animated colour (see `alphaFromSlot`). */
  slot: Slot | null;
}

/** Per-skeleton socket registry + the one `afterUpdateWorldTransforms` hook that drives them. */
const socketsBySpine = new WeakMap<Spine, Set<BoneSocket>>();
const scratch = new Matrix();

function applyBone(socket: BoneSocket): void {
  const { bone, follower, slot } = socket;
  if (follower.destroyed) return;
  // Same mapping Pixi uses for slot objects: bone world matrix → child-of-Spine local transform.
  scratch.set(bone.a, bone.c, -bone.b, -bone.d, bone.worldX, bone.worldY);
  follower.setFromMatrix(scratch);
  // The slot's colour is already the clip's animated value, blended if a mix is in flight.
  if (slot) follower.alpha = slot.color.a;
}

function ensureHook(spine: Spine): Set<BoneSocket> {
  let set = socketsBySpine.get(spine);
  if (set) return set;
  set = new Set();
  socketsBySpine.set(spine, set);
  const previous = spine.afterUpdateWorldTransforms;
  spine.afterUpdateWorldTransforms = (object: Spine): void => {
    previous(object);
    for (const socket of set!) applyBone(socket);
  };
  return set;
}

/**
 * Socket `child` to `boneName` on `spine` so it rides that bone's animation — the
 * `socketChildToBone` (`sp.SpineSocket`). A FOLLOWER container (a child of the Spine) takes the
 * bone's full world transform every frame; `child` hangs under it at `scale`, so its on-screen size
 * is the caller's, not the bone's. Bone-based (not a slot object) on purpose: a slot object is hidden
 * whenever its slot's colour alpha is 0, and the placeholder slots this is used with animate exactly
 * that. Returns the follower, or null (with a warning) when the bone does not exist.
 *
 * `alphaFromSlot` opts into the other half of that trade: the follower's ALPHA tracks the named slot's
 * animated colour, so a child that stands in for a hidden slot's art fades exactly as the clip authored
 * it (the cannon's fired round vanishing on the blast) instead of a hand-written tween. The child still
 * rides the bone, so alpha 0 hides it without the slot-object rules.
 *
 * Shared by every effect that pins live containers to bones: the win-amount digits, the cannon's
 * queue rounds, the big-win number, the feature panels' text blocks.
 */
export function socketContainerToBone(spine: Spine, boneName: string, child: Container, scale = 1, options: { alphaFromSlot?: string; log?: Log } = {}): Container | null {
  const log = options.log ?? NULL_LOG;
  const bone = spine.skeleton.findBone(boneName);
  if (!bone) {
    log(`[SpineSocket] bone "${boneName}" not found on the skeleton`);
    return null;
  }
  let slot: Slot | null = null;
  if (options.alphaFromSlot) {
    slot = spine.skeleton.findSlot(options.alphaFromSlot);
    if (!slot) log(`[SpineSocket] slot "${options.alphaFromSlot}" not found on the skeleton`);
  }
  const follower = new Container({ label: `socket:${boneName}` });
  child.removeFromParent();
  child.position.set(0, 0);
  child.scale.set(scale);
  follower.addChild(child);
  spine.addChild(follower);
  const socket: BoneSocket = { follower, bone, slot };
  ensureHook(spine).add(socket);
  applyBone(socket);
  return follower;
}
